import { useAction, useAtom, useCtx } from '@reatom/npm-react'
import { useDrag } from '@use-gesture/react'
import { motion, useMotionValue, useTransform } from 'motion/react'
import { useCallback, useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'

import { CloseIcon } from '@blade/ui-kit'
import { TOPBAR_HEIGHT } from '@root/shared/ui-kit/utils/page-layout'
import { navigationExpandedAtom } from '@root/widgets/layout/desktop-layout/sidebar/atoms'

import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from '../../layout/desktop-layout/constants'
import {
  closeInstantTradeDialogAction,
  instantTradeDialogPositionAtom,
  instantTradeDialogTabAtom,
  instantTradeDialogVariantAtom,
  instantTradeDialogVisibleAtom,
} from '../atoms'
import { INSTANT_TRADE_DIALOG_DIMENSIONS } from '../constants'

import { InstantTradeDialogTabs } from './instant-trade-dialog-tabs'
import { InstantTradeSettings } from './instant-trade-settings'
import { InstantTradeSwap } from './instant-trade-swap/instant-trade-swap'
import { InstantTradeWallets } from './instant-trade-wallets'
import { useInstantDialogHotkeys } from './use-instant-dialog-hotkeys'

import type { Pair } from '@root/entities/pair'
import type { MotionValue } from 'motion/react'
import type React from 'react'

const BOTTOM_THRESHOLD = 20
const MARGIN = 10

type Props = {
  pair: Pair.PairMinimal
}

export const InstantTradeDialog: React.FC<Props> = ({ pair }) => {
  const [expanded] = useAtom(navigationExpandedAtom)
  const [activeTab, setActiveTab] = useAtom(instantTradeDialogTabAtom)
  const [isVisible] = useAtom(instantTradeDialogVisibleAtom)
  const [_, setVariant] = useAtom(instantTradeDialogVariantAtom)

  const closeInstantTradeDialog = useAction(closeInstantTradeDialogAction)
  const ctx = useCtx()

  useInstantDialogHotkeys()

  const xValue = useMotionValue(ctx.get(instantTradeDialogPositionAtom)?.x ?? 0)
  const yValue = useMotionValue(ctx.get(instantTradeDialogPositionAtom)?.y ?? 0)
  const isApproachingBottomValue = useMotionValue(false)
  const isDraggingValue = useMotionValue(false)

  const sidebarWidth = useMemo(
    () => (expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH),
    [expanded],
  )

  const syncVisualPosition = useCallback(() => {
    const stored = ctx.get(instantTradeDialogPositionAtom)
    const maxX = window.innerWidth - INSTANT_TRADE_DIALOG_DIMENSIONS.width
    const maxY = window.innerHeight - INSTANT_TRADE_DIALOG_DIMENSIONS.height

    xValue.set(Math.min(Math.max(stored?.x ?? 0, sidebarWidth + MARGIN), maxX))
    yValue.set(Math.min(Math.max(stored?.y ?? 0, TOPBAR_HEIGHT), maxY))
  }, [ctx, xValue, yValue, sidebarWidth])

  useEffect(() => {
    syncVisualPosition()
  }, [syncVisualPosition])

  useEffect(() => {
    const handleResize = () => {
      const stored = ctx.get(instantTradeDialogPositionAtom)
      const storedX = stored?.x ?? 0
      const storedY = stored?.y ?? 0

      const maxX = window.innerWidth - INSTANT_TRADE_DIALOG_DIMENSIONS.width
      const maxY = window.innerHeight - INSTANT_TRADE_DIALOG_DIMENSIONS.height

      const clampedX = Math.min(
        Math.max(storedX, SIDEBAR_COLLAPSED_WIDTH + MARGIN),
        maxX,
      )
      const clampedY = Math.min(Math.max(storedY, TOPBAR_HEIGHT), maxY)

      if (clampedX !== storedX || clampedY !== storedY) {
        instantTradeDialogPositionAtom(ctx, { x: clampedX, y: clampedY })
      }

      syncVisualPosition()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [ctx, syncVisualPosition])

  const bind = useDrag(
    (state) => {
      isDraggingValue.set(state.dragging ?? false)
      const storedPosition = ctx.get(instantTradeDialogPositionAtom)
      const offset = [
        (storedPosition?.x ?? 0) + state.movement[0],
        (storedPosition?.y ?? 0) + state.movement[1],
      ] as const

      const clampedX = Math.min(
        Math.max(offset[0], sidebarWidth + MARGIN),
        window.innerWidth - INSTANT_TRADE_DIALOG_DIMENSIONS.width - MARGIN,
      )
      const clampedY = Math.min(
        Math.max(offset[1], TOPBAR_HEIGHT + MARGIN),
        window.innerHeight - INSTANT_TRADE_DIALOG_DIMENSIONS.height - MARGIN,
      )

      xValue.set(clampedX)
      yValue.set(clampedY)

      const approachingBottom =
        offset[1] >=
        window.innerHeight -
          INSTANT_TRADE_DIALOG_DIMENSIONS.height -
          BOTTOM_THRESHOLD

      isApproachingBottomValue.set(approachingBottom)

      if (!state.dragging) {
        instantTradeDialogPositionAtom(ctx, {
          x: clampedX,
          y: clampedY,
        })
        if (approachingBottom) {
          setVariant('bottom')
        }
      }
    },
    { filterTaps: true, preventDefault: true },
  )

  const boxShadow = useTransform(
    [isDraggingValue, isApproachingBottomValue] as unknown as MotionValue<
      [boolean, boolean]
    >,
    ([isDragging, isApproachingBottom]) => {
      if (isApproachingBottom) {
        return '0px 0px 0px 2px var(--primary-9)'
      }
      if (isDragging) {
        return '0px 1px 10px 5px var(--appearance-1)'
      }

      return 'none'
    },
  )

  const highlightOpacity = useTransform(
    isApproachingBottomValue,
    (isApproachingBottom) => (isApproachingBottom ? 1 : 0),
  )

  if (!isVisible) {
    return undefined
  }

  const renderScreen = () =>
    match(activeTab)
      .with('swap', () => <InstantTradeSwap />)
      .with('wallets', () => <InstantTradeWallets pair={pair} />)
      .with('settings', () => <InstantTradeSettings />)
      .otherwise(() => undefined)

  return (
    <>
      <Root
        style={{
          x: xValue,
          y: yValue,
          boxShadow,
        }}
        {...(bind() as {})}
      >
        <Close size={20} onClick={() => closeInstantTradeDialog()} />
        <Body>
          <InstantTradeDialogTabs
            active={activeTab}
            onTabChange={setActiveTab}
          />
          <Screen $isFirst={activeTab === 'swap'}>{renderScreen()}</Screen>
        </Body>
      </Root>
      <BottomHighlight style={{ opacity: highlightOpacity }} />
    </>
  )
}

// TODO: Ivan - Safari performance drawbacks
const Root = styled(motion.div)`
  cursor: grab;
  border: 1px solid var(--backgroundA-3);
  background: var(--background-1);

  width: ${INSTANT_TRADE_DIALOG_DIMENSIONS.width}px;
  height: ${INSTANT_TRADE_DIALOG_DIMENSIONS.height}px;

  border-radius: 14px;
  padding: 4px;

  position: fixed;
  left: 0;
  top: 0;
  transition: box-shadow 0.3s ease-in-out;
  touch-action: none;

  &[data-highlighted='true'] {
    box-shadow: 0px 0px 0px 2px var(--primary-9);
  }

  will-change: box-shadow, transform;
`

const Close = styled(CloseIcon)`
  color: var(--textA-8);

  transition: color 0.2s ease-in-out;
  cursor: pointer;

  position: absolute;
  top: 8px;
  right: 8px;

  &:hover {
    color: var(--text-12);
  }
`

const Body = styled.div`
  background: var(--background-1);

  display: grid;
  grid-template-rows: auto 1fr;

  height: 100%;
`

const Screen = styled.div<{ $isFirst: boolean }>`
  border: 1px solid var(--background-5);
  color: var(--text-11);
  background: var(--background-2);

  border-radius: 12px;
  font-size: 12px;

  display: grid;
  min-height: 0;

  ${({ $isFirst }) =>
    $isFirst &&
    css`
      border-top-left-radius: 0;
    `}
`

const BottomHighlight = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 94px;
  background: linear-gradient(180deg, transparent, var(--primaryA-3));
  pointer-events: none;
  border-bottom: 2px solid var(--primaryA-9);

  opacity: 0;
  transition: opacity 0.2s ease-in-out;
`
